import type { ReactNode } from "react";
import { Icon } from "@/components/icons";

export type AlertTone = "info" | "success" | "warning" | "danger";

/**
 * Brand inline alert / callout — a bordered, tinted box with a leading icon and
 * optional bold title. Replaces ad-hoc `text-red-600` warning paragraphs and the
 * re-inlined `role="alert"` banners scattered across forms/modals so notices read
 * as a clear, consistent component (not loose colored text). Tokens only.
 */
const TONES: Record<
  AlertTone,
  { container: string; icon: string; iconClass: string; titleClass: string; bodyClass: string }
> = {
  info: {
    container: "border-grey-200 bg-grey-50",
    icon: "info",
    iconClass: "text-grey-600",
    titleClass: "text-black",
    bodyClass: "text-grey-900",
  },
  success: {
    container: "border-green-400 bg-green-100",
    icon: "check_circle",
    iconClass: "text-green-700",
    titleClass: "text-green-700",
    bodyClass: "text-green-700",
  },
  warning: {
    container: "border-yellow-700 bg-yellow-100",
    icon: "warning",
    iconClass: "text-yellow-700",
    titleClass: "text-black",
    bodyClass: "text-grey-900",
  },
  danger: {
    container: "border-red-500 bg-red-100",
    icon: "error",
    iconClass: "text-red-600",
    titleClass: "text-red-600",
    bodyClass: "text-red-600",
  },
};

export function Alert({
  tone = "info",
  title,
  icon,
  role,
  children,
  className = "",
}: {
  tone?: AlertTone;
  /** Optional bold heading above the body. */
  title?: string;
  /** Override the default tone icon (Material Symbols name). */
  icon?: string;
  /** ARIA role — pass "alert" for dynamic error messages so SRs announce them. */
  role?: "alert" | "status";
  children?: ReactNode;
  className?: string;
}) {
  const t = TONES[tone];
  return (
    <div
      role={role}
      className={`flex items-start gap-2 rounded-[4px] border px-3 py-2 ${t.container} ${className}`}
    >
      <Icon name={icon ?? t.icon} size={20} className={`mt-0.5 shrink-0 ${t.iconClass}`} />
      <div className="flex min-w-0 flex-col gap-0.5">
        {title && <p className={`text-small font-semibold ${t.titleClass}`}>{title}</p>}
        {children && <div className={`text-small ${t.bodyClass}`}>{children}</div>}
      </div>
    </div>
  );
}
